import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { topics } from '@/mocks/topics';
import { formatCount, statusStyle } from '@/utils/format';

const hotTags = ['剪辑争议', '退团传闻', '官方回应', '时间线', '版权争议', '隐私边界'];

export default function HomeHero() {
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();
  const featured = topics[0];
  const featureStyle = statusStyle(featured.status);
  const today = new Date().toLocaleDateString('zh-CN', {
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/topics?q=${encodeURIComponent(value)}` : '/topics');
  };

  return (
    <section className="border-b border-background-200 bg-background-50">
      <div className="w-full px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="rise-in lg:col-span-7">
            <p className="flex items-center gap-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="live-dot h-2 w-2 rounded-full bg-primary-500" aria-hidden="true" />
                <span className="text-primary-600">讨论区实时更新</span>
              </span>
              <span className="text-foreground-300">|</span>
              <span>{today}</span>
            </p>

            <h1 className="mt-5 font-heading text-3xl font-black leading-tight tracking-tight text-foreground-950 md:text-5xl">
              把每一次热点，
              <br />
              吃成一条有来龙去脉的线索
            </h1>

            <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
              围绕热点事实与相关人物，汇集全网讨论与讨论区实时动态。我们先把时间线写清楚，再看当事人怎么回应、网友怎么评——吃瓜也能吃得明明白白。
            </p>

            <form onSubmit={submit} className="mt-7 flex max-w-lg flex-col gap-2 sm:flex-row">
              <div className="flex flex-1 items-center gap-2 border border-background-300 bg-background-100 px-3">
                <i className="ri-search-line text-foreground-500" aria-hidden="true" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  aria-label="搜索热议话题"
                  placeholder="搜一个你正在追的热点话题"
                  className="h-11 w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                className="h-11 whitespace-nowrap bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜一搜
              </button>
            </form>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">大家在看：</span>
              {hotTags.map((tag) => (
                <Link
                  key={tag}
                  to={`/topics?q=${encodeURIComponent(tag)}`}
                  className="cursor-pointer border border-background-300 px-2.5 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-heading text-sm font-bold text-foreground-950">此刻最热</h2>
              <Link to="/topics" className="text-xs text-foreground-500 transition-colors hover:text-primary-600">
                进入话题广场 <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>

            <Link
              to={`/topic/${featured.id}`}
              className="group block overflow-hidden border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
            >
              <div className="relative h-52 w-full overflow-hidden bg-background-200 md:h-60">
                <img
                  src={featured.cover}
                  alt={`${featured.title} 话题配图`}
                  title={`${featured.title} - ${featured.categoryName}`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-primary-950/25 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs ${featureStyle.badge}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${featureStyle.dot}`} aria-hidden="true" />
                    {featured.status}
                  </span>
                  <h3 className="mt-2 font-heading text-lg font-bold leading-snug text-background-50">
                    {featured.title}
                  </h3>
                </div>
              </div>
              <div className="flex items-center justify-between px-4 py-3 text-xs text-foreground-500">
                <span className="flex items-center gap-3">
                  <span className="flex items-center gap-1">
                    <i className="ri-fire-line text-primary-500" aria-hidden="true" />
                    热度 {formatCount(featured.heat)}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line" aria-hidden="true" />
                    {formatCount(featured.comments)}
                  </span>
                </span>
                <span>{featured.updatedAt}</span>
              </div>
            </Link>

            <ul className="mt-4 divide-y divide-background-200 border border-background-200 bg-background-50">
              {topics.slice(1, 5).map((topic, index) => (
                <li key={topic.id}>
                  <Link to={`/topic/${topic.id}`} className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-background-100">
                    <span className="font-heading text-sm font-bold text-primary-500">
                      {String(index + 2).padStart(2, '0')}
                    </span>
                    <span className="line-clamp-1 flex-1 text-sm text-foreground-800">{topic.title}</span>
                    <i className="ri-arrow-right-s-line shrink-0 text-foreground-400" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}